import { Injectable } from '@angular/core';
import { ISourceSystemType } from '../../../foundation/models/enums/source-system-type.enum';
import { TransactionService } from '../../../titleregistration/services/transaction.service';
import { VehicleMediatorService } from '../../../titleregistration/services/vehicle-mediator.service';
import { titleRegistrationRoutes } from '../../../titleregistration/titleregistration-route-lookups';
import { appInjector } from '../../app-injector';
import { TabObject } from '../../components/tabs/tabs.model';
import { IRoute } from '../../models/iroute.interface';
import { RouteBuilderService } from './route-builder.service';

@Injectable()
/**
 * Builds transaction routes for title & registration
 */
export class TitleRegistrationRouteBuilderService extends RouteBuilderService {

    private titleRegistrationRoutes: any;

    private _transactionService: TransactionService;
    private _vehicleMediatorService: VehicleMediatorService;

    constructor() {
        super();
        this.titleRegistrationRoutes = titleRegistrationRoutes;
    }

    public get sourceSystemType(): ISourceSystemType {
        return this.sourceSystemTypes.TitleRegistration;
    }

    /**
     * Gets the VehicleMediatorService
     */
    public get vehicleMediatorService(): VehicleMediatorService {
        if (this._vehicleMediatorService == undefined) {
            const injector = appInjector();
            if (injector != undefined) {
                this._vehicleMediatorService = <VehicleMediatorService> injector.get(VehicleMediatorService);
            }
        }

        return this._vehicleMediatorService;
    }

    /**
     * Gets the TransactionService
     */
    public get transactionService(): TransactionService {
        if (this._transactionService == undefined) {
            const injector = appInjector();
            if (injector != undefined) {
                this._transactionService = <TransactionService> injector.get(TransactionService);
            }
        }

        return this._transactionService;
    }

    /**
     * Gets the route list for the transaction type provided.
     * @returns: Array of routes for transaction.
     */
    getRouteList(businessTransactionTypeId: number): any[] {
        let routeList: IRoute[] = [];

        switch (businessTransactionTypeId) {
            case 1: {
                return this.getTitleOnlyRoutes();
            }
            case 2: {
                return this.getRegistrationOnlyRoutes();
            }
            case 3: {
                return this.getTitleRegistrationRoutes();
            }
            case 5: {
                return this.getIssueTemporaryPlacardRoutes();
            }
            case 6: {
                return this.getReplaceTemporaryPlacardRoutes();
            }
            case 7: {
                return this.getRenewPermanentPlacardRoutes();
            }
            case 8: {
                return this.getCancelTemporaryPlacardRoutes();
            }
            case 9: {
                return this.getIssuePermanentPlacardRoutes();
            }
            case 10: {
                return this.getReplacePermanentPlacardRoutes();
            }
            case 11: {
                return this.getCancelPermanentPlacardRoutes();
            }
            case 12: {
                return this.getIssuePermitRoutes();
            }
            case 13: {
                return this.getDuplicatePermitRoutes();
            }
            case 14: {
                return this.getCancelPermitRoutes();
            }
            case 15: {
                return this.getDuplicateTitleRoutes();
            }
            case 16: {
                return this.getCorrectTitleRoutes();
            }
            case 17: {
                return this.getCancelTitleRoutes();
            }
            case 18: {
                return this.getRegistrationRenewRoutes();
            }
            case 19: {
                return this.getDuplicateRegistrationRoutes();
            }
            case 20: {
                return this.getUpdateRegistrationRoutes();
            }
            case 21: {
                return this.getSuspendRegistrationRoutes();
            }
            case 25: {
                routeList = this.getSoldNoticeRoutes();
                break;
            }
            case 26: {
                routeList = this.getRemoveSoldNoticeRoutes();
                break;
            }
            case 33: {
                return this.getReinstateRegistrationRoutes();
            }
            case 40: {
                return this.getIssueGFDecalRoutes();
            }
            case 41: {
                return this.getReplaceGFDecalRoutes();
            }
            case 42: {
                return this.getRenewGFDecalRoutes();
            }
            case 51: {
                return this.getHomemadeVehiclePermitRoutes();
            }
            case 52: {
                return this.getCancelRegistrationRoutes();
            }

            case 64: {
                return this.getRequestRegistrationCreditRoutes();
            }
            case 69: {
                return this.getVehicleMVRRoutes();
            }
            case 74: {
                return this.getTabReplacementRoutes();
            }
            case 1080: {
                return this.getCancelTRPRoutes();
            }
            case 1124: {
                return this.getTnRCorrectionsRoutes();
            }
            case 1125: {
                return this.getRecreateRegistrationOnlyRoutes();
            }
            case 1133: {
                return this.getRecreateTRPRoutes();
            }
            case 1134: {
                return this.getDuplicatePlateRoutes();
            }
            case 1137: {
                return this.getCorrectionRoutes();
            }
            case 1192: {
                return this.getVehicleInspectionRoutes();
            }
            default: {
            }
        }

        return routeList;
    }

    private getVehicleInspectionRoutes(): TabObject[] {
        return [
            { name: 'documents', title: 'Documents', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: false },
            { name: 'title-registration-vehicle-display', title: 'Vehicle', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false },
            { name: 'customer-display', title: 'Customer', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false }
        ];
    }

    /**
     * Gets the route list for the Title Only transaction.
     * @returns: Array of routes for Title Only.
     */
    private getTitleOnlyRoutes(): TabObject[] {
        return [{ name: 'title-only', title: 'Title', isActive: true, isHidden: false, route: null, hasTooltip: false, hasValidation: false },
            { name: 'vehicleownership', title: 'Owners', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false },
            { name: 'title-registration-vehicle', title: 'Vehicle', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false },
            { name: 'documents', title: 'Documents', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: false }
        ];
    }

    /**
     * Gets the route list for the Title Only transaction.
     * @returns: Array of routes for Title Only.
     */
    private getRecreateTitleOnlyRoutes(): TabObject[] {
        return [{ name: 'recreate-title-only', title: 'Recreate Title', isActive: true, isHidden: false, route: null, hasTooltip: false, hasValidation: false },
            { name: 'vehicleownership', title: 'Owners', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false },
            { name: 'title-registration-vehicle', title: 'Vehicle', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false },
            { name: 'documents', title: 'Documents', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: false }
        ];
    }

    /**
    * Gets the route list for the Manage Liens And Brands transaction.
    * @returns: Array of routes for Manage Liens And Brands.
    */
    private getCorrectTitleRoutes(): TabObject[] {
        return [
            { name: 'correct-title-title', title: 'Title', isActive: true, isHidden: false, route: null, hasTooltip: false, hasValidation: false },
            { name: 'correct-title-vehicle-ownership', title: 'Ownership', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false },
            { name: 'correct-title-vehicle', title: 'Vehicle', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false },
            { name: 'documents', title: 'Documents', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: false }
        ];
    }

    /**
     * Gets the route list for the Duplicate Title transaction.
     * @returns: Array of routes for Duplicate Title.
     */
    private getDuplicateTitleRoutes(): TabObject[] {
        return [{ name: 'duplicate-title', title: 'Title', isActive: true, isHidden: false, route: null, hasTooltip: false, hasValidation: false }
        ];
    }

    /**
     * Gets the route list for the Issue Permit transaction.
     * @returns: Array of routes for Issue Permit.
     */
    private getIssuePermitRoutes(): TabObject[] {
        return [{ name: 'issue-permit-permit', title: 'Permit', isActive: true, isHidden: false, route: null, hasTooltip: false, hasValidation: false },
            { name: 'issue-permit-vehicle', title: 'Vehicle', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false },
            { name: 'issue-permit-customer', title: 'Customer', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false }];
    }

    /**
     * Gets the route list for the Homemade Vehicle Permit transaction.
     * @returns: Array of routes for Homemade Vehicle Permit
     */
    private getHomemadeVehiclePermitRoutes(): TabObject[] {
        return [{ name: 'issue-permit-homemade', title: 'Permit', isActive: true, isHidden: false, route: null, hasTooltip: false, hasValidation: false },
            { name: 'issue-permit-homemade-vehicle', title: 'Vehicle', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false },
            { name: 'issue-permit-homemade-customer', title: 'Customer', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false }];
    }

    /**
     * Gets the route list for the Renew Permanent Placard transaction.
     * @returns: Array of routes for Renew Permanent Placard.
     */
    private getCancelPermitRoutes(): TabObject[] {
        return [{ name: 'cancel-permit-vehicle', title: 'Vehicle', isActive: true, isHidden: false, route: null, hasTooltip: true, hasValidation: false },
            { name: 'cancel-permit-permit', title: 'Permit', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: false },
            { name: 'cancel-permit-customer', title: 'Customer', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false }
        ];
    }

    /**
     * Gets the route list for the Issue Permit transaction.
     * @returns: Array of routes for Issue Permit.
     */
    private getSoldNoticeRoutes(): IRoute[] {
        return [{ routeName: titleRegistrationRoutes.vehicle, breadcrumbName: 'Vehicle', useRouteParameter: true, pageTitle: 'Sold Notice' },
            { routeName: titleRegistrationRoutes.issuesoldnotice, validation: this.transactionService.validateTransaction, context: this.navigationService, breadcrumbName: 'Ownership & Sold Notice', useRouteParameter: true, pageTitle: 'Sold Notice' },
            { routeName: titleRegistrationRoutes.confirmation, breadcrumbName: 'Confirmation', nextButtonOverrideName: 'Confirm & Pay', showFees: true, cartContext: this.cartService, pageTitle: 'Sold Notice', useRouteParameter: true },
            { routeName: this.financeRoutes.checkout }];
    }

    /**
     * Gets the route list for the Issue Permit transaction.
     * @returns: Array of routes for Issue Permit.
     */
    private getRemoveSoldNoticeRoutes(): IRoute[] {
        return [{ routeName: titleRegistrationRoutes.vehicleremovesoldnotice, breadcrumbName: 'Vehicle', useRouteParameter: true, pageTitle: 'Remove Sold Notice' },
            { routeName: titleRegistrationRoutes.removesoldnotice, validation: this.transactionService.validateTransaction, context: this.navigationService, breadcrumbName: 'Ownership & Sold Notice', useRouteParameter: true, pageTitle: 'Remove Sold Notice' },
            { routeName: titleRegistrationRoutes.confirmation, breadcrumbName: 'Confirmation', nextButtonOverrideName: 'Confirm & Pay', showFees: true, cartContext: this.cartService, pageTitle: 'Remove Sold Notice', useRouteParameter: true },
            { routeName: this.financeRoutes.checkout }];
    }

    /**
     * Gets the route list for picking a vehicle before starting a transaction.
     * @returns: Array of routes for picking a vehicle first.
     */
    getVehicleFirstRoute(): IRoute {
        return { routeName: titleRegistrationRoutes.vehiclefirst, validation: this.vehicleMediatorService.validateVehicle, feeCalculation: null, context: this.vehicleMediatorService, breadcrumbName: null, showNext: true };
    }

    /**
     * Gets the route list for the Registration Only transaction.
     * @returns: Array of routes for Registration Only.
     */
    private getRegistrationOnlyRoutes(): TabObject[] {
        return [{ name: 'registration-only', title: 'Registration', isActive: true, isHidden: false, route: null, hasTooltip: false, hasValidation: false },
            { name: 'vehicleownership', title: 'Owners', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false },
            { name: 'title-registration-vehicle', title: 'Vehicle', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false },
            { name: 'documents', title: 'Documents', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: false }
        ];
    }

    /**
     * Gets the route list for the Registration Only transaction.
     * @returns: Array of routes for Registration Only.
     */
    private getRecreateRegistrationOnlyRoutes(): TabObject[] {
        return [{ name: 'recreate-transaction', title: 'ReCreate', isActive: true, isHidden: false, route: null, hasTooltip: false, hasValidation: false },
            { name: 'vehicleownership', title: 'Owners', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false },
            { name: 'title-registration-vehicle', title: 'Vehicle', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false },
            { name: 'documents', title: 'Documents', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: false }
        ];
    }

    /**
     * Gets the route list for the Registration Renew transaction.
     * @returns: Array of routes for Registration Renew.
     */
    private getRegistrationRenewRoutes(): TabObject[] {
        return [{ name: 'registration-renew', title: 'Renew Registration', isActive: true, isHidden: false, route: null, hasTooltip: false, hasValidation: false },
            { name: 'vehicleownership', title: 'Owners', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false },
            { name: 'title-registration-vehicle-fuel-type', title: 'Vehicle', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false },
            { name: 'documents', title: 'Documents', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: false }
        ];
    }

    /**
     * Gets the route list for the Issue Temporary Placard transaction.
     * @returns: Array of routes for Issue Temporary Placard.
     */
    private getIssueTemporaryPlacardRoutes(): TabObject[] {
        return [{ name: 'issue-temporary-placard', title: 'Placard', isActive: true, isHidden: false, route: null, hasTooltip: false, hasValidation: false },
            { name: 'placard-customer', title: 'Customer', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: false },
            { name: 'documents', title: 'Documents', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: false }
        ];
    }

    /**
     * Gets the route list for the Issue Temporary Placard transaction.
     * @returns: Array of routes for Issue Temporary Placard.
     */
    private getIssuePermanentPlacardRoutes(): TabObject[] {
        return [{ name: 'issue-permanent-placard', title: 'Placard', isActive: true, isHidden: false, route: null, hasTooltip: false, hasValidation: false },
            { name: 'placard-customer', title: 'Customer', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: false },
            { name: 'documents', title: 'Documents', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: false }
        ];

    }

    /**
     * Gets the route list for the Replace Permanent Placard transaction.
     * @returns: Array of routes for Replace Permanent Placard.
     */
    private getReplacePermanentPlacardRoutes(): TabObject[] {
        return [
            { name: 'placard-customer', title: 'Customer', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: false },
            // { name: 'documents', title: 'Documents', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: false }
        ];
    }

    /**
     * Gets the route list for the Replace Temporary Placard transaction.
     * @returns: Array of routes for Replace Temporary Placard.
     */
    private getReplaceTemporaryPlacardRoutes(): TabObject[] {
        return [
            { name: 'placard-customer', title: 'Customer', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: false },
            // { name: 'documents', title: 'Documents', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: false }
        ];
    }

    /**
     * Gets the route list for the Renew Permanent Placard transaction.
     * @returns: Array of routes for Renew Permanent Placard.
     */
    private getRenewPermanentPlacardRoutes(): TabObject[] {
        return [{ name: 'issue-permanent-placard', title: 'Placard', isActive: true, isHidden: false, route: null, hasTooltip: false, hasValidation: false },
            { name: 'placard-customer', title: 'Customer', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: false },
            { name: 'documents', title: 'Documents', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: false }
        ];
    }

    /**
     * Gets the route list for the Renew Permanent Placard transaction.
     * @returns: Array of routes for Renew Permanent Placard.
     */
    private getCancelPermanentPlacardRoutes(): TabObject[] {
        return [
            { name: 'cancel-placard', title: 'Placard', isActive: true, isHidden: false, route: null, hasTooltip: false, hasValidation: false }
        ];
    }

    /**
     * Gets the route list for the Renew Permanent Placard transaction.
     * @returns: Array of routes for Renew Permanent Placard.
     */
    private getCancelTemporaryPlacardRoutes(): TabObject[] {
        return [
            { name: 'cancel-placard', title: 'Placard', isActive: true, isHidden: false, route: null, hasTooltip: false, hasValidation: false }
        ];
    }

    /**
     * Gets the route list for the Renew Permanent Placard transaction.
     * @returns: Array of routes for Renew Permanent Placard.
     */
    private getDuplicatePermitRoutes(): TabObject[] {
        return [{ name: 'duplicate-permit-vehicle', title: 'Vehicle', isActive: true, isHidden: false, route: null, hasTooltip: true, hasValidation: false },
            { name: 'duplicate-permit-permit', title: 'Permit', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: false },
            { name: 'duplicate-permit-customer', title: 'Customer', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false }
        ];
    }

    /**
     * Gets the route list for the Title and Registration transaction.
     * @returns: Array of routes for Title and Registration.
     */
    private getTitleRegistrationRoutes(): TabObject[] {
        return [{ name: 'title-registration', title: 'Title and Registration', isActive: true, isHidden: false, route: null, hasTooltip: false, hasValidation: false },
            { name: 'vehicleownership', title: 'Owners', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false },
            { name: 'title-registration-vehicle', title: 'Vehicle', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false },
            { name: 'documents', title: 'Documents', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: false }
        ];
    }

    /**
     * Gets the route list for the Suspend Registration transaction.
     * @returns: Array of routes for Suspend Registration.
     */
    private getSuspendRegistrationRoutes(): TabObject[] {
        /* let transactionService: TransactionService = this.injector.get(TransactionService);

        return [
            { routeName: titleRegistrationRoutes.registrationviewonly, validation: transactionService.validateTransaction, feeCalculation: transactionService.calculateFees, context: this.navigationService, breadcrumbName: 'Registration', nextButtonOverrideName: 'Save & Confirm', useRouteParameter: true, pageTitle: 'Suspend Registration' },
            { routeName: titleRegistrationRoutes.confirmation, breadcrumbName: 'Confirmation', nextButtonOverrideName: 'Confirm & Pay', cartContext: this.cartService, showFees: true, useRouteParameter: true, pageTitle: 'Suspend Registration' },
            { routeName: this.financeRoutes.checkout }];*/

        return [{ name: 'registration-view-only', title: 'Registration', isActive: true, isHidden: false, route: null, hasTooltip: false, hasValidation: false },
            { name: 'duplicate-registration-vehicle-ownership', title: 'Owners', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false },
            { name: 'title-registration-vehicle-display', title: 'Vehicle', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false },
            { name: 'documents', title: 'Documents', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: false }
        ];
    }

    /**
     * Gets the route list for the Duplicate Registration transaction.
     * @returns: Array of routes for Duplicate Registration.
     */
    private getDuplicateRegistrationRoutes(): TabObject[] {
        return [
            { name: 'duplicate-registration', title: 'Duplicate Registration', isActive: true, isHidden: false, route: null, hasTooltip: false, hasValidation: false },
            { name: 'duplicate-registration-vehicle-ownership', title: 'Owners', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false },
            { name: 'title-registration-vehicle-display', title: 'Vehicle', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false },
            { name: 'documents', title: 'Documents', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: false }
        ];
    }

    /**
     * Gets the route list for the Duplicate Registration transaction.
     * @returns: Array of routes for Duplicate Registration.
     */
    private getTabReplacementRoutes(): TabObject[] {
        return [
            { name: 'tab-replacement', title: 'Tab Replacement', isActive: true, isHidden: false, route: null, hasTooltip: false, hasValidation: false },
            { name: 'duplicate-registration-vehicle-ownership', title: 'Owners', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false },
            { name: 'title-registration-vehicle-display', title: 'Vehicle', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false },
            { name: 'documents', title: 'Documents', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: false }
        ];
    }

    private getDuplicatePlateRoutes(): TabObject[] {
        return [
            { name: 'duplicate-plate', title: 'Duplicate Plate', isActive: true, isHidden: false, route: null, hasTooltip: false, hasValidation: false },
            { name: 'duplicate-registration-vehicle-ownership', title: 'Owners', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false },
            { name: 'title-registration-vehicle-display', title: 'Vehicle', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false },
            { name: 'documents', title: 'Documents', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: false }
        ];
    }

    /**
    * Gets the route list for the Reinstate Registration transaction.
    * @returns: Array of routes for Reinstate Registration.
    */
    private getReinstateRegistrationRoutes(): TabObject[] {
        return [
            { name: 'reinstate-registration', title: 'Reinstate Registration', isActive: true, isHidden: false, route: null, hasTooltip: false, hasValidation: false },
            { name: 'vehicleownership', title: 'Owners', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false },
            { name: 'title-registration-vehicle-display', title: 'Vehicle', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false }, // 71895
            { name: 'documents', title: 'Documents', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: false }
        ];
    }

    /**
    * Gets the route list for the Cancel Registration transaction.
    * @returns: Array of routes for cancel Registration.
    */
    private getCancelRegistrationRoutes(): TabObject[] {
        return [
            { name: 'cancel-registration', title: 'Cancel Registration', isActive: true, isHidden: false, route: null, hasTooltip: false, hasValidation: false },
            { name: 'cancel-registration-vehicle-ownership', title: 'Owners', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false },
            { name: 'title-registration-vehicle-display', title: 'Vehicle', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false },
            { name: 'documents', title: 'Documents', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: false }
        ];
    }

    /**
    * Gets the route list for the Update Registration transaction.
    * @returns: Array of routes for Update Registration.
    */
    private getUpdateRegistrationRoutes(): TabObject[] {
        return [
            { name: 'update-registration', title: 'Update Registration', isActive: true, isHidden: false, route: null, hasTooltip: false, hasValidation: false },
            { name: 'vehicleownership', title: 'Owners', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false },
            { name: 'title-registration-vehicle-fuel-type', title: 'Vehicle', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false }, // 71895
            { name: 'documents', title: 'Documents', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: false }
        ];
    }

    private getCancelTitleRoutes(): TabObject[] {
        return [
            { name: 'cancel-title', title: 'Cancel Title', isActive: true, isHidden: false, route: null, hasTooltip: false, hasValidation: false }
        ];
    }

    /**
     * Gets the route list for the Issue Game and Fish Decal transaction.
     * @returns: Array of routes for Issue Game and Fish Decal.
     */
    private getIssueGFDecalRoutes(): TabObject[] {
        return [];
    }

    /**
     * Gets the route list for the Replace Game and Fish Decal transaction.
     * @returns: Array of routes for Replace Game and Fish Decal.
     */
    private getReplaceGFDecalRoutes(): TabObject[] {
        return [
            { name: 'ohv-decal-view-only', title: 'Registration', isActive: true, isHidden: false, route: null, hasTooltip: false, hasValidation: false }
        ];
    }

    /**
     * Gets the route list for the Renew Game and Fish Decal transaction.
     * @returns: Array of routes for Replace Game and Fish Decal.
     */
    private getRenewGFDecalRoutes(): TabObject[] {
        return [
            { name: 'ohv-decal-view-only', title: 'Registration', isActive: true, isHidden: false, route: null, hasTooltip: false, hasValidation: false }
        ];
    }

    private getRequestRegistrationCreditRoutes(): TabObject[] {
        return [
            { name: 'request-registration-credit', title: 'Request Registration Credit', isActive: true, isHidden: false, route: null, hasTooltip: false, hasValidation: false },
            { name: 'duplicate-registration-vehicle-ownership', title: 'Owners', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false },
            { name: 'title-registration-vehicle-display', title: 'Vehicle', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false },
            { name: 'documents', title: 'Documents', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: false }
        ];
    }

    // vehicleMVR routes
    private getVehicleMVRRoutes(): TabObject[] {
        return [
            { name: 'vehicle-mvr', title: 'Vehicle MVR', isActive: true, isHidden: false, route: null, hasTooltip: false, hasValidation: false },
            { name: 'vehicle-mvr-vehicle-ownership', title: 'Owners', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false },
            { name: 'title-registration-vehicle-display', title: 'Vehicle', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false },
            { name: 'documents', title: 'Documents', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: false }
        ];
    }

    private getCancelTRPRoutes(): TabObject[] {
        return [
            { name: 'cancel-trp', title: 'TRP', isActive: true, isHidden: false, route: null, hasTooltip: false, hasValidation: false }
        ];
    }

    private getTnRCorrectionsRoutes(): TabObject[] {
        return [
            { name: 'title-registration-correction', title: 'Title Registration', isActive: true, isHidden: false, route: null, hasTooltip: false, hasValidation: false },
            { name: 'vehicle-ownership-correction', title: 'Owners', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false },
            { name: 'title-registration-vehicle', title: 'Vehicle', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false },
            { name: 'documents', title: 'Documents', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: false }
        ];
    }

    private getRecreateTRPRoutes(): TabObject[] {
        return [
            { name: 'recreate-trp', title: 'Permit', isActive: true, isHidden: false, route: null, hasTooltip: false, hasValidation: false },
            { name: 'issue-permit-vehicle', title: 'Vehicle', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false },
            { name: 'issue-permit-customer', title: 'Customer', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false }
        ];
    }

    private getCorrectionRoutes(): TabObject[] {
        return [
            { name: 'title-correction', title: 'Title', isActive: true, isHidden: false, route: null, hasTooltip: false, hasValidation: false },
            { name: 'ownership-correction', title: 'Owners', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false },
            { name: 'registration-correction', title: 'Registration', isActive: false, isHidden: false, route: null, hasTooltip: true, hasValidation: false },
            { name: 'documents', title: 'Documents', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: false }
        ];
    }
}
